import * as XLSX from 'xlsx';

const flattenPivot = (pivot) => {
    const rows = [];
    (pivot?.rows || []).forEach((row) => {
        (row.cells || []).forEach((cell) => {
            rows.push({
                Heure: row.hour,
                Date: cell.date,
                Valeur: cell.value != null ? cell.value : cell.display,
            });
        });
    });
    return rows;
};

const downloadCsvSheet = (rows, filename) => {
    const sheet = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Data');
    XLSX.writeFile(wb, filename, { bookType: 'csv' });
};

export const exportWeatherCsv = ({ precipitation, temperature, fileBase = 'weather_details' }) => {
    downloadCsvSheet(flattenPivot(precipitation), `${fileBase}_precipitation.csv`);
    downloadCsvSheet(flattenPivot(temperature), `${fileBase}_temperature.csv`);
};

export const exportWeatherExcel = ({
    precipitation,
    temperature,
    summary,
    stats,
    fileBase = 'weather_details',
}) => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(flattenPivot(precipitation)), 'Precipitation');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(flattenPivot(temperature)), 'Temperature');
    if (summary) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([summary]), 'Summary');
    }
    if (stats) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([stats]), 'Stats');
    }
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
};

export const exportWeatherPdf = () => {
    window.print();
};

const cellExportValue = (cell, valueFallback) => {
    if (cell?.value != null && Number.isFinite(cell.value)) {
        return String(cell.value).replace('.', ',');
    }
    if (cell != null && typeof cell === 'object' && cell.display && cell.display !== '—') {
        return String(cell.display).replace('.', ',');
    }
    return valueFallback;
};

/** TSV lignes Date / Heure / Valeur (compatible Excel, comme WeatherDataSections). */
export const pivotToRowTsv = (pivot, valueHeader, valueFallback = '') => {
    if (!pivot?.rows?.length) return '';

    const entries = [];
    pivot.rows.forEach((row) => {
        (row.cells || []).forEach((cell) => {
            const date = String(cell?.date || '').trim();
            const hour = String(row.hour || cell?.hour || '').trim();
            if (!date || !hour) return;
            if (cell?.value == null) return;
            entries.push({ date, hour, cell });
        });
    });

    if (entries.length === 0) return '';

    entries.sort((a, b) => {
        const dateCmp = a.date.localeCompare(b.date);
        if (dateCmp !== 0) return dateCmp;
        return a.hour.localeCompare(b.hour);
    });

    const lines = entries.map(({ date, hour, cell }) => [
        date,
        hour,
        cellExportValue(cell, valueFallback),
    ].join('\t'));

    return [['Date', 'Heure', valueHeader].join('\t'), ...lines].join('\n');
};

/** Copie texte avec repli execCommand (comme WeatherDataSections). */
export const writeClipboard = async (text) => {
    if (!text) return false;

    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // fallback ci-dessous
        }
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, text.length);

    let ok = false;
    try {
        ok = document.execCommand('copy');
    } catch {
        ok = false;
    }

    document.body.removeChild(textArea);
    return ok;
};

const pivotCellDisplay = (cell) => {
    if (cell != null && typeof cell === 'object') {
        return cell.display ?? '—';
    }
    return cell ?? '—';
};

/** TSV matrice Heure × Dates — même disposition que le tableau affiché (collage Excel). */
export const pivotToTsv = (pivot) => {
    if (!pivot?.dates?.length || !pivot?.rows?.length) return '';
    const header = ['Heure', ...pivot.dates].join('\t');
    const lines = pivot.rows.map((row) => {
        const cells = (row.cells || []).map((cell) => pivotCellDisplay(cell));
        return [row.hour, ...cells].join('\t');
    });
    return [header, ...lines].join('\r\n');
};

/** Copie un tableau HTML + TSV (Excel conserve les colonnes via text/html). */
export const copyTableElementToClipboard = async (tableEl, plainText) => {
    if (!tableEl) return false;

    const html = tableEl.outerHTML;
    const text = plainText || tableEl.innerText || '';

    if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([html], { type: 'text/html' }),
                    'text/plain': new Blob([text], { type: 'text/plain' }),
                }),
            ]);
            return true;
        } catch {
            // repli ci-dessous
        }
    }

    const onCopy = (event) => {
        event.clipboardData.setData('text/html', html);
        event.clipboardData.setData('text/plain', text);
        event.preventDefault();
    };

    document.addEventListener('copy', onCopy);
    const range = document.createRange();
    range.selectNodeContents(tableEl);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    let ok = false;
    try {
        ok = document.execCommand('copy');
    } catch {
        ok = false;
    }

    selection.removeAllRanges();
    document.removeEventListener('copy', onCopy);

    if (!ok && text) {
        return writeClipboard(text);
    }

    return ok;
};
